import React, { useState, useMemo } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import AdminDownbar from '../../components/ui/AdminDownbar';
import Icon from '../../components/AppIcon';
import { useCategories } from '../../hooks/useCategories';
import Input from '../../components/ui/Input';

const AdminCategoryManagementScreen = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Usamos el hook centralizado para toda la lógica de categorías.
  const {
    categories,
    loading,
    error,
    addCategory,
    updateCategory,
    deleteCategory,
    clearError,
  } = useCategories();

  const filteredCategories = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    
    let filtered = [...categories];
    
    // Aplicar filtro de búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(category => 
        category.name_category.toLowerCase().includes(query)
      );
    }
    
    return filtered.sort((a, b) => a.id_category - b.id_category);
  }, [categories, searchQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    const name = form.name.value.trim();

    if (!name) {
      alert('Por favor, introduce el nombre de la categoría.');
      return;
    }

    const data = {
      name_category: name,
    };

    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id_category, data);
      } else {
        await addCategory(data);
      }
      setIsModalOpen(false);
      setEditingCategory(null);
    } catch (err) {
      console.error('Error al enviar el formulario:', err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-[120px] lg:pb-0">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <AdminSidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          userName="Administrador"
        />
      </div>
      {/* Mobile Bottom Bar */}
      <div className="lg:hidden">
        <AdminDownbar />
      </div>
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div
          className="p-6 border-b border-border backdrop-blur-sm"
          style={{ backgroundColor: '#fbfbfb' }}
        >
          <div className="max-w-7xl mx-auto">
            {/* Encabezado con ícono y título */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-[#c375461a]">
                    <Icon name="Layers" size={24} className="text-[#c37546]" />
                  </div>
                  <h1 className="text-2xl font-heading font-bold text-card-foreground">
                    Gestión de Categorías
                  </h1>
                </div>
                <p className="text-sm italic text-muted-foreground mt-2">
                  “Todo tiene su tiempo, y todo lo que se quiere debajo del cielo tiene su hora.” — Eclesiastés 3:1
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    setEditingCategory(null);
                    setIsModalOpen(true);
                  }}
                  className="bg-primary text-white px-4 py-2 rounded flex items-center gap-2 shadow-sm hover:bg-primary/90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  aria-label="Agregar nueva categoría"
                >
                  <Icon name="Plus" size={16} />
                  <span className="font-medium">Nueva categoría</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Search Bar Above Table */}
            <div className="px-4 sm:px-6 lg:px-0 mb-6">
              <div className="relative w-full">
                <Input
                  type="search"
                  placeholder="Buscar categorías..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 w-full"
                />
                <Icon 
                  name="Search" 
                  size={18} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                />
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3">
                  <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                  <span className="text-muted-foreground">Cargando categorías...</span>
                </div>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 text-red-600">
                    {error}
                    <button onClick={clearError} className="ml-2 underline text-sm">Limpiar</button>
                  </div>
                )}
          <div className="w-full max-w-screen-lg mx-auto overflow-x-auto border border-gray-200 p-4 sm:p-6 rounded-lg bg-white shadow-sm">
            <table
              className="min-w-[320px] w-full text-[12px] sm:text-[14px] table-auto"
              style={{ fontFamily: '"Poppins", Arial, sans-serif' }}
            >
              <thead className="bg-[#0c3454]">
                <tr>
                  <th className="px-4 py-2 sm:px-6 sm:py-3 text-left text-white font-semibold text-[13px] sm:text-[15px] border-r border-gray-300">Nombre</th>
                  <th className="px-2 py-2 sm:px-4 sm:py-3 text-left text-white font-semibold text-[13px] sm:text-[15px]">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(filteredCategories) && filteredCategories.map((category, index) => (
                  <tr
                    key={category.id_category}
                    className="border-b border-gray-200 transition-colors duration-200 hover:bg-[#e8f4f8]"
                    style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff' }}
                  >
                    <td className="px-4 py-2 sm:px-6 sm:py-3 border-r border-gray-200 font-semibold text-[#2d3748]">
                      {category.name_category}
                    </td>
                    <td className="px-2 py-2 sm:px-4 sm:py-3 flex gap-2 items-center justify-start">
                      {/* Botón Editar */}
                      <button
                        onClick={() => { setEditingCategory(category); setIsModalOpen(true); }}
                        className="p-2 sm:px-3 sm:py-2 text-white bg-[#c37546] hover:bg-[#a85d3a] rounded transition-colors duration-200 flex items-center justify-center sm:justify-start gap-0 sm:gap-1"
                        title="Editar"
                      >
                        <Icon name="Edit" size={16} />
                        <span className="hidden sm:inline text-sm font-medium">Editar</span>
                      </button>

                      {/* Botón Eliminar */}
                      <button
                        onClick={async () => {
                          try {
                            await deleteCategory(category.id_category, category.name_category);
                          } catch (error) {
                            // Usuario canceló la eliminación
                          }
                        }}
                        className="p-2 sm:px-3 sm:py-2 bg-red-600 text-white hover:bg-red-700 rounded transition-colors duration-200 flex items-center justify-center sm:justify-start gap-0 sm:gap-1"
                        title="Eliminar"
                      >
                        <Icon name="Trash" size={16} />
                        <span className="hidden sm:inline text-sm font-medium">Eliminar</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
              </>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{fontFamily: 'Georgia, "Times New Roman", serif'}}>
            <h2 className="text-xl font-semibold mb-4">{editingCategory ? 'Editar categoría' : 'Nueva categoría'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4" style={{fontFamily: 'Georgia, "Times New Roman", serif'}}>
              <div className="p-4 rounded-lg" style={{backgroundColor: '#f8f9fa'}}>
                <h3 className="text-lg font-semibold mb-3" style={{color: '#0c3454'}}>Información de la categoría</h3>
                <div className="space-y-4">
                  <input type="text" name="name" defaultValue={editingCategory?.name_category || ''} placeholder="Nombre de la categoría" className="w-full border border-gray-300 rounded-lg px-4 py-3 transition-all" style={{fontFamily: 'Georgia, "Times New Roman", serif'}} onFocus={(e) => {e.target.style.borderColor = '#c37546'; e.target.style.boxShadow = 'none'}} onBlur={(e) => {e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'}} />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                <button type="button" onClick={() => { setIsModalOpen(false); setEditingCategory(null); }} className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all flex items-center gap-2 shadow-sm" style={{fontFamily: 'Georgia, "Times New Roman", serif'}}>
                  <Icon name="X" size={16} /> Cancelar
                </button>
                <button type="submit" className="px-6 py-3 text-white rounded-lg font-medium transition-all flex items-center gap-2 shadow-lg" style={{backgroundColor: '#0c3454', fontFamily: 'Georgia, "Times New Roman", serif'}} onMouseEnter={(e) => e.target.style.backgroundColor = '#c37546'} onMouseLeave={(e) => e.target.style.backgroundColor = '#0c3454'}>
                  <Icon name={editingCategory ? 'Save' : 'Plus'} size={16} /> {editingCategory ? 'Actualizar Categoría' : 'Crear Categoría'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCategoryManagementScreen;

